import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { Formik } from 'formik';
import Button from '../components/Button';
import { Field, FieldInput, FieldLabel } from '../components/Field';
import Loader from '../components/Loader';
import { Modal, ModalExit, ModalName, ModalPart } from '../components/Modal';
import { useToast } from '../components/Toast';
import { updateSpaceCache } from '../utilities/cache';
import { SPACE } from '../utilities/queries';
import { UPDATE_SPACE_NAME } from '../utilities/mutations';
import { useSpaceContext } from '../contexts/space';

const EditSpaceNameModal = () => {
  const navigate = useNavigate();
  const params = useParams();
  const toast = useToast();
  const { spaceVars } = useSpaceContext();
  const { loading, data } = useQuery(SPACE, {
    variables: { slug: params.s, ...spaceVars },
  });
  const [updateSpaceName] = useMutation(UPDATE_SPACE_NAME);

  const close = () =>
    !loading && navigate(`/s/${data.space.slug}/settings/main`);

  return (
    <Modal onClose={() => close()}>
      {loading ? (
        <ModalPart>
          <Loader />
        </ModalPart>
      ) : (
        <>
          <ModalExit />
          <ModalName>Edit space name</ModalName>
          <Formik
            initialValues={{ name: data.space.name }}
            onSubmit={async (values) => {
              try {
                await updateSpaceName({
                  variables: {
                    name: values.name,
                    id: data.space.id,
                  },
                  update: (cache) => updateSpaceCache(cache, values.name),
                });
              } catch (error) {
                toast({ children: error.message });

                return;
              }

              toast({ children: 'Space name updated' });
              close();
            }}
          >
            {({
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <ModalPart>
                  <Field>
                    <FieldLabel>Space name</FieldLabel>
                    <FieldInput
                      type="name"
                      name="name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name}
                      data-autofocus
                    />
                  </Field>
                </ModalPart>
                <ModalPart>
                  <Button
                    type="submit"
                    disabled={values.name === data.space.name || isSubmitting}
                    isPrimary
                    isExtended
                  >
                    Save
                  </Button>
                </ModalPart>
              </form>
            )}
          </Formik>
        </>
      )}
    </Modal>
  );
};

export default EditSpaceNameModal;
